import type { StateCreator } from 'zustand';
import type { Strategy, StrategyDetail, AIFeedback, ModuleVersion, RealityCheckEntry, CurrentProfile, StrategyDocument, BootstrapPhase, BootstrapAnalysis, BootstrapOption, BootstrapNicheRequest, CascadeStatus, ModuleConfig, TranslateProgressEvent, TranslateCompleteEvent } from '../types';
import { strategyApi } from '../services/strategyApi';

export interface AutofillReview {
  active: boolean;
  currentModule: string | null;
  completedModules: string[];
  pendingData: Record<string, unknown> | null;
}

export interface StrategySlice {
  strategies: Strategy[];
  activeStrategyId: number | null;
  activeStrategy: StrategyDetail | null;
  currentProfile: CurrentProfile | null;
  strategiesLoading: boolean;
  aiLoading: boolean;
  aiFeedback: AIFeedback | null;
  autoFillLoading: boolean;
  autofillReview: AutofillReview;
  autofillUndo: { moduleId: string; previousWorkingData: Record<string, unknown> } | null;
  autofillError: string | null;
  pendingSync: boolean;
  lastSyncAt: string | null;
  versionHistory: ModuleVersion[];
  versionHistoryModule: string | null;
  realityCheckHistory: RealityCheckEntry[];
  historyLoading: boolean;
  historyTotal: number;
  snapshotView: RealityCheckEntry | null;
  documents: StrategyDocument[];
  documentsLoading: boolean;
  uploadProgress: { filename: string; status: 'pending' | 'uploading' | 'done' | 'error' }[];

  fetchStrategies: (status?: string) => Promise<void>;
  selectStrategy: (id: number) => Promise<void>;
  createStrategy: (data: {
    name: string;
    niche?: string;
    category?: string;
    account_language?: string;
    source_expansion_run_id?: number;
    assign_insight_ids?: number[];
    suggestion_index?: number;
  }) => Promise<Strategy>;
  deleteStrategy: (id: number) => Promise<void>;
  updateStrategyName: (id: number, name: string) => Promise<void>;
  updateStrategyStatus: (id: number, status: string) => Promise<void>;
  updateField: (moduleId: string, key: string, value: unknown) => void;
  syncToBackend: () => Promise<void>;
  iterateModule: (moduleId: string) => Promise<void>;
  triggerRealityCheck: (moduleId: string, model?: string) => Promise<void>;
  triggerAutofill: (startFromModule?: string) => Promise<void>;
  triggerAutofillModule: (moduleId: string, mode: 'regenerate' | 'improve', feedback?: string) => Promise<void>;
  undoAutofillModule: () => void;
  acceptAutofillModule: (moduleId: string, nextModule: string | null) => void;
  cancelAutofillReview: () => void;
  loadVersionHistory: (moduleId: string) => Promise<void>;
  restoreVersion: (moduleId: string, version: number) => Promise<unknown>;
  clearAIFeedback: () => void;
  loadRealityCheckHistory: (moduleId: string, offset?: number) => Promise<void>;
  setSnapshotView: (entry: RealityCheckEntry | null) => void;
  fetchDocuments: (strategyId: number) => Promise<void>;
  uploadDocuments: (strategyId: number, files: File[]) => Promise<void>;
  deleteDocument: (strategyId: number, docId: number) => Promise<void>;
  reprocessDocument: (strategyId: number, docId: number) => Promise<void>;

  // Cascade status fields
  cascadeStatus: CascadeStatus | null;
  cascadeFailedModule: string | null;
  cascadeErrorDetail: string | null;
  moduleConfig: Record<string, ModuleConfig> | null;
  fetchModuleConfig: () => Promise<void>;
  retryFromModule: (moduleId: string) => void;

  // Translation state fields
  translating: boolean;
  translateProgress: { module_id: string; progress: number; total: number } | null;
  translateSkipped: string[];
  translateUndoSnapshot: { modules_data: Record<string, unknown>; account_language: string } | null;
  translateUndoTimer: ReturnType<typeof setTimeout> | null;

  // Translation actions
  translateStrategy(targetLanguage: string): Promise<void>;
  handleTranslateProgress(data: TranslateProgressEvent): void;
  handleTranslateComplete(data: TranslateCompleteEvent): void;
  undoTranslation(): Promise<void>;
  dismissTranslateUndo(): void;

  // Bootstrap state fields
  bootstrapPhase: BootstrapPhase;
  bootstrapNiche: string;
  bootstrapBriefing: string;
  bootstrapAnalysis: BootstrapAnalysis | null;
  bootstrapChosenOption: BootstrapOption | null;
  bootstrapCustomDirection: string | null;
  bootstrapLoading: boolean;
  bootstrapError: string | null;

  // Bootstrap actions
  startBootstrap(): void;
  analyzeNiche(niche: string, briefing: string): Promise<void>;
  reanalyzeNiche(feedback: string): Promise<void>;
  chooseOption(option: BootstrapOption): Promise<void>;
  chooseCustomDirection(text: string): Promise<void>;
  completeBootstrap(): void;
  resetBootstrap(): void;

  // One-shot prefill handed from ExpansionAdvisor → StrategyOS → BootstrapWizard.
  // Transient; not persisted (store has no persist middleware).
  pendingBootstrapPrefill: {
    strategyId: number;
    niche: string;
    briefing: string;
    language: 'de' | 'en' | 'es';
  } | null;
  setPendingBootstrapPrefill(prefill: NonNullable<StrategySlice['pendingBootstrapPrefill']>): void;
  clearPendingBootstrapPrefill(): void;

  // Refocus · guided focus-shift (preview → apply → strength-check)
  refocusActive: boolean;
  refocusLoading: boolean;
  refocusError: string | null;
  refocusProposal: {
    proposed_modules: Record<string, Record<string, unknown>>;
    per_module_mode: Record<string, 'regenerate' | 'improve'>;
  } | null;
  refocusStrength: Record<string, AIFeedback> | null;
  refocusStrengthLoading: boolean;
  openRefocus(): void;
  closeRefocus(): void;
  runRefocusPreview(focus: string, documentIds?: number[]): Promise<void>;
  applyRefocus(): Promise<Record<string, number> | null>;
  discardRefocusProposal(): void;
  runRefocusStrengthCheck(): Promise<void>;

  // Import · merge a parsed strategy patch (from the Markdown round-trip) back in.
  importModules(patch: Record<string, Record<string, unknown>>): Promise<boolean>;
}

export const createStrategySlice: StateCreator<StrategySlice> = (set, get) => ({
  strategies: [],
  activeStrategyId: null,
  activeStrategy: null,
  currentProfile: null,
  strategiesLoading: false,
  aiLoading: false,
  aiFeedback: null,
  autoFillLoading: false,
  autofillReview: { active: false, currentModule: null, completedModules: [], pendingData: null },
  autofillUndo: null,
  autofillError: null,
  pendingSync: false,
  lastSyncAt: null,
  versionHistory: [],
  versionHistoryModule: null,
  realityCheckHistory: [],
  historyLoading: false,
  historyTotal: 0,
  snapshotView: null,
  documents: [],
  documentsLoading: false,
  uploadProgress: [],

  // Cascade initial state
  cascadeStatus: null,
  cascadeFailedModule: null,
  cascadeErrorDetail: null,
  moduleConfig: null,

  fetchModuleConfig: async () => {
    if (get().moduleConfig) return; // already cached
    try {
      const config = await strategyApi.getModulesConfig();
      set({ moduleConfig: config as Record<string, ModuleConfig> });
    } catch {
      // non-critical, ignore
    }
  },

  retryFromModule: (moduleId) => {
    set({ cascadeStatus: null, cascadeFailedModule: null, cascadeErrorDetail: null });
    get().triggerAutofill(moduleId);
  },

  // Translation initial state
  translating: false,
  translateProgress: null,
  translateSkipped: [],
  translateUndoSnapshot: null,
  translateUndoTimer: null,

  translateStrategy: async (targetLanguage) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    // Create snapshot for undo (deep copy)
    const snapshot = {
      modules_data: JSON.parse(JSON.stringify(activeStrategy.modules_data)),
      account_language: activeStrategy.account_language,
    };
    set({ translating: true, translateProgress: null, translateSkipped: [], translateUndoSnapshot: snapshot, translateUndoTimer: null });
    try {
      await strategyApi.translate(activeStrategy.id, targetLanguage);
    } catch {
      set({ translating: false, translateProgress: null });
    }
  },

  handleTranslateProgress: (data) => {
    set({ translateProgress: { module_id: data.module_id, progress: data.progress, total: data.total } });
  },

  handleTranslateComplete: (data) => {
    const { activeStrategy, translateUndoTimer } = get();
    if (translateUndoTimer) clearTimeout(translateUndoTimer);
    // Start 30s undo timer
    const timer = setTimeout(() => {
      set({ translateUndoSnapshot: null, translateUndoTimer: null });
    }, 30000);
    set({ translating: false, translateProgress: null, translateSkipped: data.skipped, translateUndoTimer: timer });
    // Reload strategy to get updated data
    if (activeStrategy) {
      get().selectStrategy(activeStrategy.id);
    }
  },

  undoTranslation: async () => {
    const { activeStrategy, translateUndoSnapshot, translateUndoTimer } = get();
    if (!activeStrategy || !translateUndoSnapshot) return;
    if (translateUndoTimer) clearTimeout(translateUndoTimer);
    // Patch snapshot back
    await strategyApi.update(activeStrategy.id, {
      modules_data: translateUndoSnapshot.modules_data,
      account_language: translateUndoSnapshot.account_language,
    } as never);
    set({ translateUndoSnapshot: null, translateUndoTimer: null, translateSkipped: [] });
    // Reload strategy
    get().selectStrategy(activeStrategy.id);
  },

  dismissTranslateUndo: () => {
    const { translateUndoTimer } = get();
    if (translateUndoTimer) clearTimeout(translateUndoTimer);
    set({ translateUndoSnapshot: null, translateUndoTimer: null });
  },

  // Bootstrap initial state
  bootstrapPhase: 'idle',
  bootstrapNiche: '',
  bootstrapBriefing: '',
  bootstrapAnalysis: null,
  bootstrapChosenOption: null,
  bootstrapCustomDirection: null,
  bootstrapLoading: false,
  bootstrapError: null,

  // Transient prefill payload from ExpansionAdvisor → StrategyOS handoff.
  pendingBootstrapPrefill: null,
  setPendingBootstrapPrefill: (prefill) => set({ pendingBootstrapPrefill: prefill }),
  clearPendingBootstrapPrefill: () => set({ pendingBootstrapPrefill: null }),

  fetchStrategies: async (status) => {
    set({ strategiesLoading: true });
    try {
      const strategies = await strategyApi.list(status);
      set({ strategies, strategiesLoading: false });
    } catch {
      set({ strategiesLoading: false });
    }
  },

  selectStrategy: async (id) => {
    set({ activeStrategyId: id });
    try {
      const strategy = await strategyApi.get(id);
      const profile = await strategyApi.getCurrentProfile(id).catch(() => null);
      // Backfill: if strategy.niche is set but niche_name is missing in workingData, populate it
      const modulesData = strategy.modules_data as Record<string, { workingData?: Record<string, unknown> }>;
      if (strategy.niche && !modulesData?.niche?.workingData?.niche_name) {
        const nicheModule = modulesData?.niche || { workingData: {} };
        strategy.modules_data = {
          ...modulesData,
          niche: { ...nicheModule, workingData: { ...nicheModule.workingData, niche_name: strategy.niche } },
        } as typeof strategy.modules_data;
      }
      set({ activeStrategy: strategy, currentProfile: profile });
    } catch {
      set({ activeStrategy: null, currentProfile: null });
    }
  },

  createStrategy: async (data) => {
    const created = await strategyApi.create(data);
    await get().fetchStrategies();
    return created;
  },

  deleteStrategy: async (id) => {
    await strategyApi.delete(id);
    const { activeStrategyId } = get();
    if (activeStrategyId === id) {
      set({ activeStrategyId: null, activeStrategy: null });
    }
    await get().fetchStrategies();
  },

  updateStrategyName: async (id, name) => {
    await strategyApi.update(id, { name } as never);
    await get().fetchStrategies();
    if (get().activeStrategyId === id) {
      await get().selectStrategy(id);
    }
  },

  updateStrategyStatus: async (id, status) => {
    await strategyApi.update(id, { status } as never);
    await get().fetchStrategies();
  },

  updateField: (moduleId, key, value) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    const modules_data = { ...activeStrategy.modules_data };
    const module = modules_data[moduleId] || { workingData: {} };
    modules_data[moduleId] = {
      ...module,
      workingData: { ...module.workingData, [key]: value },
    };
    set({ activeStrategy: { ...activeStrategy, modules_data }, pendingSync: true });
  },

  syncToBackend: async () => {
    const { activeStrategy, pendingSync } = get();
    if (!activeStrategy || !pendingSync) return;
    try {
      const modulesData = activeStrategy.modules_data as Record<string, { workingData?: Record<string, unknown> }>;

      // Auto-sync: niche_name → strategy.niche (sidebar category)
      const nicheWorkingData = modulesData?.niche?.workingData;
      const nicheName = (nicheWorkingData?.niche_name as string | undefined)?.trim() ?? null;
      const nicheChanged = nicheName !== (activeStrategy.niche ?? null) && (nicheName !== null || activeStrategy.niche != null);

      const payload: Record<string, unknown> = { modules_data: activeStrategy.modules_data };
      if (nicheChanged) payload.niche = nicheName || null;

      const updated = await strategyApi.update(activeStrategy.id, payload as never);

      // Update sidebar list locally (avoid full refetch)
      if (nicheChanged) {
        set((s) => ({
          strategies: s.strategies.map((st) =>
            st.id === activeStrategy.id ? { ...st, niche: updated.niche } : st
          ),
        }));
      }

      // Bidirectional sync: write brand identity fields back to profile
      const visualData = modulesData?.visual?.workingData;
      if (visualData) {
        const profileBound = ['brand_aesthetic', 'face_style_prompt', 'profile_pic_prompt', 'color_palette'];
        const profileUpdates: Record<string, unknown> = {};
        for (const key of profileBound) {
          if (visualData[key] !== undefined) profileUpdates[key] = visualData[key];
        }
        if (Object.keys(profileUpdates).length > 0) {
          const updatedProfile = await strategyApi.updateCurrentProfile(activeStrategy.id, profileUpdates as never).catch(() => null);
          if (updatedProfile) set({ currentProfile: updatedProfile });
        }
      }
      set({ pendingSync: false, lastSyncAt: new Date().toISOString() });
    } catch {
      // keep pendingSync=true, will retry
    }
  },

  iterateModule: async (moduleId) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    await get().syncToBackend();
    const result = await strategyApi.iterate(activeStrategy.id, moduleId);
    // Update outdated flags in local state
    const updated = { ...activeStrategy, outdated_flags: result.outdated_flags };
    set({ activeStrategy: updated, pendingSync: false });
  },

  triggerRealityCheck: async (moduleId, model) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    set({ aiLoading: true, aiFeedback: null });
    try {
      const feedback = await strategyApi.realityCheck(activeStrategy.id, moduleId, model);
      set({ aiFeedback: feedback, aiLoading: false });
    } catch {
      set({ aiLoading: false });
    }
  },

  triggerAutofill: async (startFromModule) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    set({ autoFillLoading: true, cascadeStatus: null, cascadeFailedModule: null, cascadeErrorDetail: null });
    try {
      const result = await strategyApi.autofill(activeStrategy.id, startFromModule ? { start_from_module: startFromModule } : undefined);
      const updated = { ...activeStrategy, modules_data: result.modules_data as never };
      set({
        activeStrategy: updated,
        autoFillLoading: false,
        pendingSync: false,
        cascadeStatus: (result.cascade_status as unknown as CascadeStatus) ?? null,
        cascadeFailedModule: result.failed_module ?? null,
        cascadeErrorDetail: result.error_detail ?? null,
      });
      if (get().bootstrapPhase === 'sequential') {
        if (result.failed_module) {
          // Stay in sequential phase so stepper can show failure
        } else {
          get().completeBootstrap();
        }
      }
    } catch {
      set({ autoFillLoading: false });
    }
  },

  triggerAutofillModule: async (moduleId, mode, feedback?) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    set({ autoFillLoading: true, autofillError: null });
    try {
      // Flush unsaved working state first so the backend generates from the
      // latest values (e.g. the content-module platform toggle, interview answers).
      // autofillModule reads modules_data from the DB, not from this request.
      await get().syncToBackend();
      const result = await strategyApi.autofillModule(activeStrategy.id, moduleId, mode, feedback);
      // Apply directly to workingData instead of parking in autofillReview.pendingData —
      // that review-panel path requires autofillReview.active=true, which nothing in this
      // codebase ever sets. Mirrors the working pattern in ContentStudioTab's "Archetyp mit
      // KI generieren" button: generate, then apply immediately, with an undo escape hatch.
      const { activeStrategy: latest } = get();
      if (!latest) { set({ autoFillLoading: false }); return; }
      const modules_data = { ...latest.modules_data } as Record<string, { workingData: Record<string, unknown> }>;
      const existing = modules_data[moduleId] || { workingData: {} };
      const previousWorkingData = existing.workingData;
      modules_data[moduleId] = { ...existing, workingData: result.module_data };
      set((s) => ({
        activeStrategy: { ...latest, modules_data },
        pendingSync: true,
        autoFillLoading: false,
        autofillUndo: { moduleId, previousWorkingData },
        autofillReview: {
          ...s.autofillReview,
          currentModule: moduleId,
          completedModules: s.autofillReview.completedModules.includes(moduleId)
            ? s.autofillReview.completedModules
            : [...s.autofillReview.completedModules, moduleId],
          pendingData: null,
        },
      }));
    } catch (err) {
      set({
        autoFillLoading: false,
        autofillError: err instanceof Error ? err.message : 'Generierung fehlgeschlagen.',
      });
    }
  },

  undoAutofillModule: () => {
    const { activeStrategy, autofillUndo } = get();
    if (!activeStrategy || !autofillUndo) return;
    const modules_data = { ...activeStrategy.modules_data } as Record<string, { workingData: Record<string, unknown> }>;
    const existing = modules_data[autofillUndo.moduleId] || { workingData: {} };
    modules_data[autofillUndo.moduleId] = { ...existing, workingData: autofillUndo.previousWorkingData };
    set({
      activeStrategy: { ...activeStrategy, modules_data },
      pendingSync: true,
      autofillUndo: null,
    });
  },

  acceptAutofillModule: (moduleId, nextModule) => {
    const { activeStrategy, autofillReview } = get();
    if (!activeStrategy || !autofillReview.pendingData) return;
    // Apply pending data to working state
    const modules_data = { ...activeStrategy.modules_data };
    const existing = (modules_data as Record<string, { workingData: Record<string, unknown> }>)[moduleId] || { workingData: {} };
    (modules_data as Record<string, { workingData: Record<string, unknown> }>)[moduleId] = {
      ...existing,
      workingData: autofillReview.pendingData,
    };
    set({
      activeStrategy: { ...activeStrategy, modules_data },
      pendingSync: true,
      autofillReview: {
        active: nextModule !== null,
        currentModule: nextModule,
        completedModules: [...autofillReview.completedModules, moduleId],
        pendingData: null,
      },
    });
  },

  cancelAutofillReview: () => {
    set({ autofillReview: { active: false, currentModule: null, completedModules: [], pendingData: null } });
  },

  // --- Refocus ---------------------------------------------------------------
  refocusActive: false,
  refocusLoading: false,
  refocusError: null,
  refocusProposal: null,
  refocusStrength: null,
  refocusStrengthLoading: false,

  openRefocus: () => set({ refocusActive: true, refocusError: null, refocusProposal: null, refocusStrength: null }),
  closeRefocus: () => set({ refocusActive: false, refocusLoading: false, refocusError: null, refocusProposal: null }),

  runRefocusPreview: async (focus, documentIds) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    set({ refocusLoading: true, refocusError: null, refocusProposal: null });
    try {
      // Flush unsaved edits so the proposal builds on the latest state.
      await get().syncToBackend();
      const result = await strategyApi.refocusPreview(activeStrategy.id, focus, documentIds);
      set({ refocusProposal: result, refocusLoading: false });
    } catch {
      set({ refocusLoading: false, refocusError: 'Refokussierung fehlgeschlagen. Bitte Fokus präzisieren und erneut versuchen.' });
    }
  },

  applyRefocus: async () => {
    const { activeStrategy, refocusProposal } = get();
    if (!activeStrategy || !refocusProposal) return null;
    set({ refocusLoading: true, refocusError: null });
    try {
      const result = await strategyApi.refocusApply(activeStrategy.id, refocusProposal.proposed_modules);
      // Reload the strategy so the editor reflects the new (persisted) state.
      // Keep the dialog open (refocusActive stays true) so it can run the
      // strength-check; the dialog closes itself via closeRefocus().
      await get().selectStrategy(activeStrategy.id);
      set({ refocusLoading: false, refocusProposal: null });
      return result.versioned_modules;
    } catch {
      set({ refocusLoading: false, refocusError: 'Übernehmen fehlgeschlagen.' });
      return null;
    }
  },

  discardRefocusProposal: () => set({ refocusProposal: null, refocusStrength: null }),

  importModules: async (patch) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return false;
    try {
      // Flush any unsaved edits first so the merge builds on the latest state.
      await get().syncToBackend();
      await strategyApi.importModules(activeStrategy.id, patch);
      // Reload so the editor reflects the merged + persisted state (backend also
      // snapshotted every module into the version history for rollback).
      await get().selectStrategy(activeStrategy.id);
      set({ pendingSync: false });
      return true;
    } catch {
      return false;
    }
  },

  runRefocusStrengthCheck: async () => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    set({ refocusStrengthLoading: true });
    try {
      const result = await strategyApi.refocusStrengthCheck(activeStrategy.id);
      set({ refocusStrength: result.checks, refocusStrengthLoading: false });
    } catch {
      set({ refocusStrengthLoading: false });
    }
  },

  loadVersionHistory: async (moduleId) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    const versions = await strategyApi.getVersions(activeStrategy.id, moduleId);
    set({ versionHistory: versions, versionHistoryModule: moduleId });
  },

  restoreVersion: async (moduleId, version) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    const result = await strategyApi.restoreVersion(activeStrategy.id, moduleId, version);
    // Reload strategy to get updated modules_data
    const updated = await strategyApi.get(activeStrategy.id);
    set({ activeStrategy: updated, pendingSync: false });
    return result;
  },

  clearAIFeedback: () => set({ aiFeedback: null }),

  loadRealityCheckHistory: async (moduleId, offset = 0) => {
    const { activeStrategy } = get();
    if (!activeStrategy) return;
    set({ historyLoading: true });
    try {
      const result = await strategyApi.getRealityChecks(activeStrategy.id, moduleId, 5, offset);
      if (offset > 0) {
        set((s) => ({
          realityCheckHistory: [...s.realityCheckHistory, ...result.items],
          historyTotal: result.total,
          historyLoading: false,
        }));
      } else {
        set({ realityCheckHistory: result.items, historyTotal: result.total, historyLoading: false });
      }
    } catch {
      set({ historyLoading: false });
    }
  },

  setSnapshotView: (entry) => set({ snapshotView: entry }),

  fetchDocuments: async (strategyId) => {
    set({ documentsLoading: true });
    try {
      const docs = await strategyApi.listDocuments(strategyId);
      set({ documents: docs, documentsLoading: false });
    } catch {
      set({ documentsLoading: false });
    }
  },

  uploadDocuments: async (strategyId, files) => {
    const progress = files.map((f) => ({ filename: f.name, status: 'uploading' as const }));
    set({ uploadProgress: progress });
    try {
      const results = await strategyApi.uploadDocuments(strategyId, files);
      const updatedProgress = results.map((r) => ({
        filename: r.original_filename,
        status: (r.extraction_status === 'done' ? 'done' : 'error') as 'done' | 'error',
      }));
      set({ uploadProgress: updatedProgress });
      const docs = await strategyApi.listDocuments(strategyId);
      set({ documents: docs });
    } catch {
      set({ uploadProgress: files.map((f) => ({ filename: f.name, status: 'error' as const })) });
    }
  },

  deleteDocument: async (strategyId, docId) => {
    await strategyApi.deleteDocument(strategyId, docId);
    const docs = await strategyApi.listDocuments(strategyId);
    set({ documents: docs });
  },

  reprocessDocument: async (strategyId, docId) => {
    await strategyApi.reprocessDocument(strategyId, docId);
    const docs = await strategyApi.listDocuments(strategyId);
    set({ documents: docs });
  },

  // Bootstrap actions
  startBootstrap: () => {
    set({
      bootstrapPhase: 'input',
      bootstrapNiche: '',
      bootstrapBriefing: '',
      bootstrapAnalysis: null,
      bootstrapChosenOption: null,
      bootstrapCustomDirection: null,
      bootstrapLoading: false,
      bootstrapError: null,
    });
  },

  analyzeNiche: async (niche, briefing) => {
    const { activeStrategy, bootstrapLoading } = get();
    if (!activeStrategy || bootstrapLoading) return;
    set({ bootstrapPhase: 'analyzing', bootstrapLoading: true, bootstrapError: null, bootstrapNiche: niche, bootstrapBriefing: briefing });
    try {
      const analysis = await strategyApi.bootstrapAnalyze(activeStrategy.id, niche, briefing);
      set({ bootstrapAnalysis: analysis, bootstrapPhase: 'options', bootstrapLoading: false });
    } catch (e) {
      set({ bootstrapError: e instanceof Error ? e.message : 'Analyse fehlgeschlagen', bootstrapLoading: false, bootstrapPhase: 'input' });
    }
  },

  reanalyzeNiche: async (feedback) => {
    const { activeStrategy, bootstrapNiche, bootstrapBriefing } = get();
    if (!activeStrategy) return;
    const combinedBriefing = bootstrapBriefing
      ? `${bootstrapBriefing}\n\nNutzer-Feedback / Korrektur:\n${feedback}`
      : feedback;
    set({ bootstrapPhase: 'analyzing', bootstrapLoading: true, bootstrapError: null, bootstrapBriefing: combinedBriefing });
    try {
      const analysis = await strategyApi.bootstrapAnalyze(activeStrategy.id, bootstrapNiche, combinedBriefing);
      set({ bootstrapAnalysis: analysis, bootstrapPhase: 'options', bootstrapLoading: false });
    } catch (e) {
      set({ bootstrapError: e instanceof Error ? e.message : 'Analyse fehlgeschlagen', bootstrapLoading: false, bootstrapPhase: 'options' });
    }
  },

  chooseOption: async (option) => {
    const { activeStrategy, bootstrapBriefing, bootstrapLoading } = get();
    if (!activeStrategy || bootstrapLoading) return;
    set({ bootstrapChosenOption: option, bootstrapCustomDirection: null, bootstrapPhase: 'generating', bootstrapLoading: true, bootstrapError: null });
    try {
      const requestData: BootstrapNicheRequest = {
        chosen_option: option,
        custom_direction: null,
        briefing: bootstrapBriefing,
      };
      const result = await strategyApi.bootstrapGenerateNiche(activeStrategy.id, requestData);
      // Save niche_data as the niche module's workingData
      const modules_data = { ...(activeStrategy.modules_data as Record<string, { workingData: Record<string, unknown> }>) };
      const existingNiche = modules_data['niche'] || { workingData: {} };
      modules_data['niche'] = { ...existingNiche, workingData: result.niche_data as Record<string, unknown> };
      set({
        activeStrategy: { ...activeStrategy, modules_data: modules_data as never },
        pendingSync: true,
        bootstrapPhase: 'sequential',
        bootstrapLoading: false,
      });
      // Sync niche to DB before cascade so backend reads updated niche module
      await get().syncToBackend();
      get().triggerAutofill('market');
    } catch (e) {
      set({ bootstrapError: e instanceof Error ? e.message : 'Generierung fehlgeschlagen', bootstrapLoading: false, bootstrapPhase: 'options' });
    }
  },

  chooseCustomDirection: async (text) => {
    const { activeStrategy, bootstrapBriefing, bootstrapLoading } = get();
    if (!activeStrategy || bootstrapLoading) return;
    set({ bootstrapCustomDirection: text, bootstrapChosenOption: null, bootstrapPhase: 'generating', bootstrapLoading: true, bootstrapError: null });
    try {
      const requestData: BootstrapNicheRequest = {
        chosen_option: null,
        custom_direction: text,
        briefing: bootstrapBriefing,
      };
      const result = await strategyApi.bootstrapGenerateNiche(activeStrategy.id, requestData);
      const modules_data = { ...(activeStrategy.modules_data as Record<string, { workingData: Record<string, unknown> }>) };
      const existingNiche = modules_data['niche'] || { workingData: {} };
      modules_data['niche'] = { ...existingNiche, workingData: result.niche_data as Record<string, unknown> };
      set({
        activeStrategy: { ...activeStrategy, modules_data: modules_data as never },
        pendingSync: true,
        bootstrapPhase: 'sequential',
        bootstrapLoading: false,
      });
      // Sync niche to DB before cascade so backend reads updated niche module
      await get().syncToBackend();
      get().triggerAutofill('market');
    } catch (e) {
      set({ bootstrapError: e instanceof Error ? e.message : 'Generierung fehlgeschlagen', bootstrapLoading: false, bootstrapPhase: 'options' });
    }
  },

  completeBootstrap: () => {
    set({ bootstrapPhase: 'complete' });
  },

  resetBootstrap: () => {
    set({
      bootstrapPhase: 'idle',
      bootstrapNiche: '',
      bootstrapBriefing: '',
      bootstrapAnalysis: null,
      bootstrapChosenOption: null,
      bootstrapCustomDirection: null,
      bootstrapLoading: false,
      bootstrapError: null,
    });
  },
});
